/**
 * ParticipantCard — um participante do Bond (papel operacional + permissões = ACL,
 * ADR-130). Mostra pessoa/usuário, papel, permissões, escopo, lado e versão. Para
 * participantes ATIVOS oferece editar (papel + permissões + escopo, PATCH If-Match)
 * e revogar (reason opcional via query, DELETE If-Match). Espelha o B2 RelatedPersonCard.
 */

import { useState } from 'react';

import { useRevokeParticipant, useUpdateParticipant } from '../../../hooks/use-bond-participants';

import {
  buildUpdateRequest,
  editFormFrom,
  validateEditForm,
  type ParticipantFormState,
} from './participants-form';
import {
  formatDate,
  permissionLabel,
  roleLabel,
  scopeLabel,
  sideLabel,
  statusLabel,
} from './participants-labels';
import { PERMISSION_SCOPE_OPTIONS } from './participants-options';
import { PermissionPicker } from './PermissionPicker';
import { RoleField } from './RoleField';

import type { BondParticipantWireItem } from '../../../lib/api-client';

type Mode = 'view' | 'edit' | 'revoke';

export function ParticipantCard({
  bondId,
  participant,
}: {
  bondId: string;
  participant: BondParticipantWireItem;
}): JSX.Element {
  const [mode, setMode] = useState<Mode>('view');
  const update = useUpdateParticipant(bondId);
  const revoke = useRevokeParticipant(bondId);

  const [form, setForm] = useState<ParticipantFormState>(() => editFormFrom(participant));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isActive = participant.revokedAt === null;
  const status = isActive ? 'active' : 'revoked';
  const subject = participant.userId ?? participant.personId ?? '—';
  const subjectKindLabel = participant.userId !== null ? 'Usuário interno' : 'Pessoa';

  function resetTo(next: Mode): void {
    setError(null);
    setReason('');
    setForm(editFormFrom(participant));
    setMode(next);
  }

  function handleSaveEdit(): void {
    const v = validateEditForm(form);
    if (v !== null) {
      setError(v);
      return;
    }
    setError(null);
    update.mutate(
      {
        participantId: participant.id,
        version: participant.version,
        body: buildUpdateRequest(form),
      },
      {
        onSuccess: () => setMode('view'),
        onError: (err) => setError(err.message !== '' ? err.message : 'Falha ao salvar.'),
      },
    );
  }

  function handleConfirmRevoke(): void {
    setError(null);
    const trimmed = reason.trim();
    revoke.mutate(
      {
        participantId: participant.id,
        version: participant.version,
        ...(trimmed !== '' && { reason: trimmed }),
      },
      {
        onSuccess: () => setMode('view'),
        onError: (err) => setError(err.message !== '' ? err.message : 'Falha ao revogar.'),
      },
    );
  }

  return (
    <div className={`bpt-card bpt-card--${status}`}>
      <div className="bpt-card__head">
        <span className="bpt-card__person" title={subject}>
          {subject}
        </span>
        <span className={`bpt-status bpt-status--${status}`}>{statusLabel(status)}</span>
      </div>

      <div className="bpt-card__tags">
        <span className="bpt-tag bpt-tag--role">{roleLabel(participant.role)}</span>
        <span className="bpt-tag bpt-tag--kind">{subjectKindLabel}</span>
        {participant.side !== null && (
          <span className="bpt-tag">{sideLabel(participant.side)}</span>
        )}
      </div>

      <div className="bpt-card__perms">
        {participant.permissions.length === 0 ? (
          <span className="bpt-muted">Sem permissões</span>
        ) : (
          participant.permissions.map((p) => (
            <span key={p} className="bpt-tag bpt-tag--perm">
              {permissionLabel(p)}
            </span>
          ))
        )}
      </div>

      <dl className="bpt-card__meta">
        <div>
          <dt>Escopo</dt>
          <dd>{scopeLabel(participant.permissionScope)}</dd>
        </div>
        <div>
          <dt>Concedido em</dt>
          <dd>{formatDate(participant.grantedAt)}</dd>
        </div>
        {!isActive && (
          <div>
            <dt>Revogado</dt>
            <dd>
              {formatDate(participant.revokedAt)}
              {participant.revokedReason !== null ? ` · ${participant.revokedReason}` : ''}
            </dd>
          </div>
        )}
      </dl>

      {/* Ações (apenas ativos) */}
      {isActive && mode === 'view' && (
        <div className="bpt-card__actions">
          <button type="button" className="bpt-btn bpt-btn--ghost" onClick={() => resetTo('edit')}>
            Editar papel / permissões
          </button>
          <button
            type="button"
            className="bpt-btn bpt-btn--danger"
            onClick={() => resetTo('revoke')}
          >
            Revogar
          </button>
        </div>
      )}

      {/* Editar inline */}
      {mode === 'edit' && (
        <div className="bpt-inline">
          <RoleField
            id={`edit-role-${participant.id}`}
            value={form.role}
            onChange={(role) => setForm((p) => ({ ...p, role }))}
          />
          <div className="bpt-field">
            <span className="bpt-field__label">Permissões</span>
            <PermissionPicker
              selected={form.permissions}
              onToggle={(k) =>
                setForm((p) => ({
                  ...p,
                  permissions: p.permissions.includes(k)
                    ? p.permissions.filter((x) => x !== k)
                    : [...p.permissions, k],
                }))
              }
              idPrefix={`edit-${participant.id}`}
            />
          </div>
          <label className="bpt-field" htmlFor={`edit-scope-${participant.id}`}>
            <span className="bpt-field__label">Escopo</span>
            <select
              id={`edit-scope-${participant.id}`}
              className="bpt-input bpt-select"
              value={form.permissionScope}
              onChange={(e) => setForm((p) => ({ ...p, permissionScope: e.target.value }))}
            >
              {PERMISSION_SCOPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {error !== null && (
            <p className="bpt-alert bpt-alert--error" role="alert">
              {error}
            </p>
          )}
          <div className="bpt-inline__actions">
            <button
              type="button"
              className="bpt-btn bpt-btn--ghost"
              onClick={() => setMode('view')}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="bpt-btn bpt-btn--primary"
              disabled={update.isPending}
              onClick={handleSaveEdit}
            >
              {update.isPending ? 'Salvando…' : 'Salvar alterações'}
            </button>
          </div>
        </div>
      )}

      {/* Revogar inline */}
      {mode === 'revoke' && (
        <div className="bpt-inline">
          <label className="bpt-field">
            <span className="bpt-field__label">Motivo (opcional)</span>
            <textarea
              className="bpt-textarea"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="ex: saída da conta / fim do mandato — opcional"
            />
          </label>
          {error !== null && (
            <p className="bpt-alert bpt-alert--error" role="alert">
              {error}
            </p>
          )}
          <div className="bpt-inline__actions">
            <button
              type="button"
              className="bpt-btn bpt-btn--ghost"
              onClick={() => setMode('view')}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="bpt-btn bpt-btn--danger"
              disabled={revoke.isPending}
              onClick={handleConfirmRevoke}
            >
              {revoke.isPending ? 'Revogando…' : 'Confirmar revogação'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
